/**
 * Hedita Theme Context
 * Gestione 3 palette (orange/sky/nature) + Dark/Light mode
 * Persistenza in host_profiles
 */

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useAuth } from "./useAuth";
import { supabase } from "./supabaseClient";

export type HeditaTheme = "orange" | "sky" | "nature";
export type ThemeMode = "light" | "dark" | "system";

interface ThemeContextType {
  theme: HeditaTheme;
  mode: ThemeMode;
  isDark: boolean;
  setTheme: (theme: HeditaTheme) => void;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  themes: { id: HeditaTheme; label: string; color: string }[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_OPTIONS = [
  { id: "orange" as HeditaTheme, label: "Premium Orange", color: "#f97316" },
  { id: "sky" as HeditaTheme, label: "Sky Blue", color: "#3b82f6" },
  { id: "nature" as HeditaTheme, label: "Nature Green", color: "#10b981" },
];

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  
  // Load from localStorage first (immediate, no flash)
  const savedTheme = typeof window !== 'undefined' ? localStorage.getItem('hedita-theme') as HeditaTheme : null;
  const savedMode = typeof window !== 'undefined' ? localStorage.getItem('hedita-mode') as ThemeMode : null;
  
  const [theme, setThemeState] = useState<HeditaTheme>(savedTheme || "orange");
  const [mode, setModeState] = useState<ThemeMode>(savedMode || "system");
  const [isDark, setIsDark] = useState(true);

  // Carica preferenze dal DB (override localStorage if exists)
  useEffect(() => {
    if (!currentUser?.id) return;

    const loadTheme = async () => {
      const { data, error } = await supabase
        .from("host_profiles")
        .select("current_theme, theme_mode")
        .eq("user_id", currentUser.id)
        .single();

      if (data && !error) {
        if (data.current_theme) {
          setThemeState(data.current_theme as HeditaTheme);
          localStorage.setItem('hedita-theme', data.current_theme);
        }
        if (data.theme_mode) {
          setModeState(data.theme_mode as ThemeMode);
          localStorage.setItem('hedita-mode', data.theme_mode);
        }
      }
    };

    loadTheme();
  }, [currentUser?.id]);

  // Salva preferenze nel DB
  const saveThemePreference = async (newTheme: HeditaTheme, newMode: ThemeMode) => {
    if (!currentUser?.id) return;

    await supabase
      .from("host_profiles")
      .update({
        current_theme: newTheme,
        theme_mode: newMode,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", currentUser.id);
  };

  // Applica tema al DOM
  useEffect(() => {
    const root = document.documentElement;
    
    // Rimuovi temi precedenti
    root.removeAttribute("data-theme");
    
    // Applica nuovo tema
    if (theme !== "orange") {
      root.setAttribute("data-theme", theme);
    }

    // Gestione dark/light mode
    const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const shouldBeDark = mode === "dark" || (mode === "system" && systemDark);
    
    setIsDark(shouldBeDark);
    
    if (shouldBeDark) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme, mode]);

  // Listen for system theme changes
  useEffect(() => {
    if (mode !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    
    const handleChange = () => {
      const shouldBeDark = mediaQuery.matches;
      setIsDark(shouldBeDark);
      
      if (shouldBeDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [mode]);

  const setTheme = (newTheme: HeditaTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('hedita-theme', newTheme);
    saveThemePreference(newTheme, mode);
  };

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem('hedita-mode', newMode);
    saveThemePreference(theme, newMode);
  };

  const toggleMode = () => {
    const newMode = isDark ? "light" : "dark";
    setMode(newMode);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        mode,
        isDark,
        setTheme,
        setMode,
        toggleMode,
        themes: THEME_OPTIONS,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
